import * as XLSX from "sheetjs-style";
import moment from "moment-timezone";
import { DATE_FORMAT, TIMEZONE, TIME_LINE_CHUNKS } from "@shared/constants";
import { DATE_FORMAT_TYPE, Option, ORDER_TYPE, SensorDataSessionValuesFilter } from "@shared/interfaces";
import { SensorDataScale, Station } from "@shared/models";
import { StationConnectionResult } from "@containers/Station/interfaces";

export const copyObject = <T,>(obj: T) => JSON.parse(JSON.stringify(obj));

export const openInNewTab = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

export const debounce = <F extends (...args: Parameters<F>) => ReturnType<F>>(func: F, waitFor = 500) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: Parameters<F>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced as (...args: Parameters<F>) => ReturnType<F>;
};

export const prepareSortFilter = <T,>(payload: Partial<T & { sort_by?: string[]; sort_order?: ORDER_TYPE }>) => {
  return {
    ...payload,
    sort_by: payload.sort_by && payload.sort_order ? payload.sort_by : undefined,
    sort_order: payload.sort_by && payload.sort_order ? payload.sort_order : undefined,
  };
};

export const getFormattedDate = (dateString: Date | number | string | null, format?: DATE_FORMAT_TYPE) => {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);

  const day = date.toLocaleString("en", { day: "numeric" });
  const month = date.toLocaleString("en", { month: "long" });
  const year = date.toLocaleString("en", { year: "numeric" });
  const time = date
    .toLocaleString("en", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      second: "numeric",
    })
    .toLowerCase();

  if (format && format === DATE_FORMAT_TYPE.SHORT) {
    return moment(new Date(date)).format("MM/DD/YYYY HH:mm:ss");
  }

  if (format && format === DATE_FORMAT_TYPE.SHORT_WITHOUT_TIME) {
    return moment(new Date(date)).format("MM/DD/YYYY");
  }
  return `${day} ${month} ${year} ${time}`;
};

export const getFirstLetters = (text: string) => {
  return text?.match(/\b\w/g)?.join("")?.toUpperCase();
};

export const getUniqueElements = <T,>(array: T[], key: keyof T): T[] => {
  return Array.from(new Map(array.map((item) => [item[key], item])).values());
};

export const getFullName = <T,>(
  user: Partial<T & { first_name: string | null; last_name: string | null; login?: string | null }>,
) => {
  const firstName = user.first_name || "";
  const lastName = user.last_name || "";
  if (!firstName && !lastName) return user.login || "";

  return `${firstName} ${lastName}`;
};

export const HHMMSSToSeconds = (hhmmss: string) => {
  return hhmmss.split(":").reduce((acc: number, time) => Number(60 * acc + Number(time)), 0);
};

export const secondsToHHMMSS = (sec: number) => {
  const hours = String(Math.floor(sec / 3600));
  let minutes = String(Math.floor((sec - Number(hours) * 3600) / 60));
  let seconds = String(sec - Number(hours) * 3600 - Number(minutes) * 60);

  if (Number(minutes) < 10) {
    minutes = `0${minutes}`;
  }
  if (Number(seconds) < 10) {
    seconds = `0${seconds}`;
  }

  return `${hours}:${minutes}:${seconds}`;
};

export const bytesToMegaBytes = (bytes: number): number => {
  return bytes / (1024 * 1024);
};

export const getPercentageFromFileUploading = (progressEvent: { loaded: number; total: number }) => {
  const { loaded, total } = progressEvent;
  return Math.floor((loaded * 100) / total);
};

export function downLoadFile(data: any, type: string, name: string) {
  const blob = new Blob([data], { type: type });
  const url = window.URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.download = `${name}.csv`;
  anchor.href = url;
  anchor.click();
}

export function downLoadFileFromUrl(url: string) {
  const link = document.createElement("a");
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const excelDownlod = <T,>(data: { name: string; items: T[] }[], fileName: string) => {
  const wb = XLSX.utils.book_new();

  data.forEach((d) => {
    const jsonToSheet = XLSX.utils.json_to_sheet(d.items);
    XLSX.utils.book_append_sheet(wb, jsonToSheet, d.name);
  });

  XLSX.writeFile(wb, `${fileName}.xlsx`);
};

export const getRandomColor = () => {
  return "#" + Math.floor(Math.random() * 16777215).toString(16);
};

export const getChartPrevNextTimeForDateInstance = (
  filter: SensorDataSessionValuesFilter,
  minTime: Date,
  maxTime: Date,
) => {
  const minTimeSeconds = new Date(minTime).getTime();
  const maxTimeSeconds = new Date(maxTime).getTime();

  const currentMinTimeSeconds = new Date(String(filter.start)).getTime();
  const currentMaxTimeSeconds = new Date(String(filter.end)).getTime();

  const intervalValue = (filter.interval as Option<number>).value * 1000;
  const step = intervalValue;

  const next = {
    start: currentMinTimeSeconds + step < maxTimeSeconds ? new Date(currentMinTimeSeconds + step) : "",
    end: new Date(currentMaxTimeSeconds + step),
  };

  const startPrev =
    currentMinTimeSeconds - step < minTimeSeconds ? new Date(minTimeSeconds) : new Date(currentMinTimeSeconds - step);

  let endPrev = currentMaxTimeSeconds - step > minTimeSeconds ? new Date(currentMaxTimeSeconds - step) : "";
  if (endPrev && startPrev.getTime() < minTimeSeconds) {
    endPrev = new Date(minTimeSeconds + intervalValue);
  }

  return {
    prev: {
      start: startPrev ? startPrev.toISOString() : "",
      end: endPrev ? new Date(endPrev).toISOString() : "",
    },
    next: {
      start: next.start ? new Date(next.start).toISOString() : "",
      end: next.end ? next.end.toISOString() : "",
    },
  };
};

export const getChartPrevNextTimeForStringInstance = (
  filter: SensorDataSessionValuesFilter,
  minTime: string | Date,
  maxTime: string | Date,
) => {
  if (minTime instanceof Date && maxTime instanceof Date && !isNaN(new Date(String(filter.start)).getTime())) {
    return getChartPrevNextTimeForDateInstance(filter, new Date(minTime), new Date(maxTime));
  }

  const minTimeSeconds = HHMMSSToSeconds(String(minTime));
  const maxTimeSeconds = HHMMSSToSeconds(String(maxTime));

  const currentMinTimeSeconds = HHMMSSToSeconds(String(filter.start));
  const currentMaxTimeSeconds = HHMMSSToSeconds(String(filter.end));
  const intervalValue = (filter.interval as Option<number>).value;
  const step = intervalValue;

  const startNext = currentMinTimeSeconds + step < maxTimeSeconds ? secondsToHHMMSS(currentMinTimeSeconds + step) : "";
  const endNext = secondsToHHMMSS(currentMaxTimeSeconds + step);

  const next = {
    start: startNext,
    end: endNext,
  };

  let startPrev = secondsToHHMMSS(currentMinTimeSeconds - step);
  if (HHMMSSToSeconds(startPrev) < minTimeSeconds) {
    startPrev = secondsToHHMMSS(minTimeSeconds);
  }

  let endPrev = currentMaxTimeSeconds - step > minTimeSeconds ? secondsToHHMMSS(currentMaxTimeSeconds - step) : "";
  if (endPrev && HHMMSSToSeconds(startPrev) < minTimeSeconds) {
    endPrev = secondsToHHMMSS(minTimeSeconds + intervalValue);
  }

  const prev = {
    start: startPrev,
    end: endPrev,
  };

  return {
    prev,
    next,
  };
};

export const getChartPrevNextTime = (
  filter: SensorDataSessionValuesFilter,
  minTime: string | Date,
  maxTime: string | Date,
) => {
  if (minTime instanceof Date && maxTime instanceof Date && !isNaN(new Date(String(filter.start)).getTime())) {
    return getChartPrevNextTimeForDateInstance(filter, new Date(minTime), new Date(maxTime));
  }

  return getChartPrevNextTimeForStringInstance(filter, minTime, maxTime);
};

export const getScaleFromTimeLine = (timeLine: string): string => {
  switch (timeLine) {
    case TIME_LINE_CHUNKS.FIVE_MINUTES: {
      return SensorDataScale.SECOND;
    }
    case TIME_LINE_CHUNKS.SEVEN_DAYS: {
      return SensorDataScale.HOUR;
    }
    default: {
      return SensorDataScale.MINUTE;
    }
  }
};

export const addSecondsToDate = (date: Date | string, seconds: number) => {
  return new Date(new Date(date).getTime() + seconds * 1000);
};

export const getDifferenceInDays = (a: Date, b: Date): number => {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;

  return Math.floor((new Date(b).getTime() - new Date(a).getTime()) / _MS_PER_DAY);
};

export const getTimezoneList = () => {
  const EXCLUDE_TIMEZONES = ["UCT"];

  const timezonesList = moment.tz.names();
  return timezonesList
    .filter((tz) => EXCLUDE_TIMEZONES.find((t) => !tz.includes(t)))
    .map((tz) => ({
      label: `${tz.replaceAll("_", " ")} (${moment.tz(tz).format("Z")})`,
      value: tz,
    }));
};

export const addDaysToDate = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const subDaysToDate = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

export const getFormattedDateTime = (date: Date | string) => {
  return moment(date).format("MM/DD/YYYY hh:mm A");
};

export const getStartOfDay = (date: Date | string) => {
  return moment(date).startOf("day").toDate();
};

export const getEndOfDay = (date: Date | string) => {
  return moment(date).endOf("day").toDate();
};

export const addMinutesToDate = (date: Date, minutes: number): Date => {
  const result = new Date(date);
  result.setTime(date.getTime() + minutes * 60 * 1000);
  return result;
};

export const getCurrentTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const convertDateToTimezone = (date: string | Date | null, timezone: string) => {
  if (!date) return null;
  return addMinutesToDate(new Date(date), new Date(date).getTimezoneOffset() + moment().tz(timezone).utcOffset());
};

export const formatDateToTz = (date: Date | string, tz?: string | null, format?: string) => {
  return `${moment(new Date(date)).format(format || DATE_FORMAT.GENERAL)}${moment()
    .tz(tz || TIMEZONE.UTC)
    .format("ZZ")}`;
};

export const getStartAndNowMonth = () => {
  return [moment().startOf("month").toDate(), moment().toDate()] as [Date, Date];
};

export const getPreviousMonth = () => {
  const date = moment().add(-1, "month");
  return [date.startOf("month").toDate(), date.endOf("month").toDate()] as [Date, Date];
};

export const getPreviousDay = () => {
  const date = moment().add(-1, "days");
  return [date.startOf("day").toDate(), moment().endOf("day").toDate()] as [Date, Date];
};

export const getShortDateFormat = (date: Date, dateFormat?: string) => {
  return moment(date).format(dateFormat || "YYYY-MM-DD");
};

export const getPercentageDifference = (value1: number, value2: number) => {
  const difference = Math.abs(Number(value1) - Number(value2));
  const percentageDifference = (difference / Number(value1)) * 100;
  return Number(percentageDifference).toFixed(0);
};

export const getArtifactStyles = (artifact: number) => {
  switch (artifact) {
    case 0:
      return null;
    case 1:
      return { r: 4, stroke: "red", fill: "red" };
    case 2:
      return { r: 4, stroke: "blue", fill: "blue" };
    case 3:
      return { r: 4, stroke: "green", fill: "green" };
    default: {
      return { r: 4, stroke: "red", fill: "red" };
    }
  }
};

export const file2Base64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      const [, result] = (reader.result?.toString() || "").split(",");
      resolve(result || "");
    };
    reader.onerror = (error) => reject(error);
  });
};

export const handleDownloadClick = (fileLink: string) => {
  const link = window.document.createElement("a");
  link.href = fileLink;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
};

export const getInitialTime = (time: string) => {
  const [hours, minutes] = time.split(":");

  const date = new Date();

  date.setHours(Number(hours));
  date.setMinutes(Number(minutes));

  return date;
};

export const checkSDHSync = (connection: StationConnectionResult | null) => {
  const isSynced = connection && connection?.is_synced_with_remote_sdh;
  return isSynced;
};

export const getStationTrackingTimeRange = (station: Station | null) => {
  const endTime = station?.end_time ? getInitialTime(station.end_time) : null;
  const startTime = station?.start_time ? getInitialTime(station.start_time) : null;
  const timezone = station?.timezone ? station.timezone : null;

  return {
    endTime,
    startTime,
    timezone,
  };
};
